import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaRegLightbulb, FaCalculator, FaRoad, FaHandshake } from 'react-icons/fa';

const FinancialPlanningPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Financial Planning' }
  ];

  const planningServices = [
    {
      icon: <FaRegLightbulb className="w-8 h-8 text-secondary" />,
      title: "Goal Setting",
      description: "Work with our experts to define and prioritize your financial goals for both short and long-term success."
    },
    {
      icon: <FaCalculator className="w-8 h-8 text-secondary" />,
      title: "Cash Flow Analysis",
      description: "Comprehensive analysis of your income, expenses, and cash flow to optimize your financial position."
    },
    {
      icon: <FaRoad className="w-8 h-8 text-secondary" />,
      title: "Strategic Roadmap",
      description: "Detailed financial roadmap with actionable steps to achieve your objectives efficiently."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-secondary" />,
      title: "Ongoing Support",
      description: "Continuous guidance and plan adjustments as your life circumstances and goals evolve."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Financial Planning" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/co-workers-planning-strategy.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Expert Financial Planning Services
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Segway Capital</span> Financial Planning offers expert advice and assistance on reaching 
                your financial goals and objectives. Our comprehensive approach ensures that every aspect of 
                your financial life is carefully considered and optimized for success.
              </p>
              <p className="text-lg text-gray-700">
                Get insight from our financial experts who understand the complexities of modern financial 
                markets and can guide you through the decision-making process with confidence and clarity.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/co-workers-discussing-business-project.jpg" 
                alt="Financial Planning Consultation" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {planningServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
              >
                <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  {service.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-custom mb-16"
          >
            <h3 className="text-3xl font-heading font-bold text-primary mb-6 text-center">
              Our Financial Planning Process
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">1</span>
                </div>
                <h4 className="text-xl font-semibold text-primary mb-2">Discovery</h4>
                <p className="text-gray-600">We learn about your current financial situation, goals, and concerns.</p>
              </div>
              <div className="text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">2</span>
                </div>
                <h4 className="text-xl font-semibold text-primary mb-2">Strategy</h4>
                <p className="text-gray-600">We develop a customized financial plan tailored to your unique needs.</p>
              </div>
              <div className="text-center">
                <div className="bg-secondary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-secondary">3</span>
                </div>
                <h4 className="text-xl font-semibold text-primary mb-2">Implementation</h4>
                <p className="text-gray-600">We execute the plan and provide ongoing monitoring and adjustments.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Start Your Financial Planning Journey</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Take the first step towards achieving your financial goals. Our expert planners are ready to 
                help you create a comprehensive strategy for your financial future.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Get Your Free Consultation
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FinancialPlanningPage; 